<template lang="html">
  <section :id="id" class="customer-domain-collapse-details">
    <div class="access-info">
      <toggle-button
        id="full-access"
        :label="$t('fullAccess')"
        :on="isFullAccess"
        :disabled="displayLoader"
        @on-toggle-button-change="onToggleButtonChange"
      />
    </div>
    <table class="m-table bordered no-row-hover striped" v-show="!isFullAccess">
      <thead>
        <tr>
          <th>{{ $t("customDisplayElement") }}</th>
          <th>{{ $t("access") }}</th>
        </tr>
      </thead>
      <tbody v-if="isAccessReady">
        <custom-display-element-access-row
          v-for="(widget, index) in widgets"
          :key="index"
          :id="`custom-display-element-access-${widget.uuid}`"
          :customer="customer"
          :uuid="$route.params.uuid"
          :widget="widget"
          :is-full-access="isFullAccess"
          :widget-access="getWidgetAccess(widget.uuid)"
        />
      </tbody>
    </table>
    <div class="m-group-btns">
      <button
        class="m-button neutral"
        @click.stop="onCancelClicked"
        :disabled="displayLoader"
      >
        {{ getMessageText(messages.CANCEL) }}
      </button>
      <button
        class="m-button brand"
        @click.stop="onSaveClicked"
        :disabled="disableOK"
      >
        {{ getMessageText(messages.SAVE) }}
      </button>
      <div v-if="displayLoader" class="loader"></div>
    </div>
    <div v-if="customerAccess.delete">
      <div class="overlay"></div>
      <div class="loader"></div>
    </div>
  </section>
</template>

<script lang="js">
  import { MUTATION_NAME, TOAST_OPTIONS } from "@/static/global-variables";
  import distributionChannelUtil from "@/utils/distribution-channel-util";
  import DistributionChannelsService from "@/services/distributionChannelsService";
  import ToggleButton from "@/components/ToggleButton";
  import CustomDisplayElementAccessRow from "@/components/custom-display-elements/tabs/customer-domains/CustomDisplayElementAccessRow";

  export default  {
    name: "customer-domain-collapse-details",
    components: {
      ToggleButton,
      CustomDisplayElementAccessRow
    },
    props: {
      id: String,
      customer: {
        type: Object,
        default: function() {
          return {}
        }
      }
    },
    data() {
      return {
        displayLoader: false,
        isAccessReady: false
      }
    },
    computed: {
      messages() {
        return this.$store.state.messages;
      },
      distributionchannel() {
        return this.$store.getters[MUTATION_NAME.DISTRIBUTION_CHANNEL_LATEST_WIDGETS] || {};
      },
      widgets() {
        return this.isCurrentStoreData?
        distributionChannelUtil.removeWidgetsWithOnlyUnpublishedVersion(this.distributionchannel.widgets) : [];
      },
      isCurrentStoreData() {
        return this.distributionchannel.uuid === this.$route.params.uuid;
      },
      hasChanges() {
        return this.customerAccess.update || this.isWidgetUpdate;
      },
      disableOK() {
        return !this.hasChanges || this.displayLoader;
      },
      isWidgetUpdate() {
        return Object.values(this.customerAccess).some(e => e.update);
      },
      widgetAccess() {
        return this.customer.widgetAccess || [];
      },
      selectedAccess() {
        return this.$store.getters[MUTATION_NAME.CUSTOMER_DOMAIN_WIDGET_ACCESS] || {};
      },
      customerAccess() {
        return {...this.selectedAccess[this.customer.uuid] || {}};
      },
      isFullAccess() {
        return this.customerAccess.fullAccess;
      }
    },
    mounted () {
      if(!this.customerAccess.name) {
        this.updateCustomerAccess(this.customer.uuid, {
          prevFullAccess: this.customer.fullAccess,
          fullAccess: this.customer.fullAccess,
          name: this.customer.name,
          update: false,
          isOpen: this.customerAccess.isOpen
        });
      }
      this.isAccessReady = true;
    },
    methods: {
      getWidgetAccess(widgetUuid) {
        const widgetAcccess = this.widgetAccess.find(e => e.widgetId === widgetUuid);
        return widgetAcccess? JSON.parse(JSON.stringify(widgetAcccess)) : undefined;
      },
      onToggleButtonChange(fullAccess) {
        const customerAccess = this.customerAccess;
        customerAccess.update = this.customerAccess.prevFullAccess !== fullAccess;
        customerAccess.fullAccess = fullAccess;
        this.updateCustomerAccess(this.customer.uuid, customerAccess);
        this.emitFullAccessEvent(fullAccess);
      },
      updateCustomerAccess(customer, access) {
        this.$store.commit(MUTATION_NAME.CUSTOMER_DOMAIN_WIDGET_ACCESS, { customer, access });
      },
      resetAllAccess() {
        // clear the object to reset all changes
        this.updateCustomerAccess(this.customer.uuid, {});
      },
      onCancelClicked() {
        this.emitFullAccessEvent(this.customer.fullAccess);
        this.resetAllAccess();
      },
      emitFullAccessEvent(fullAccess) {
        this.$emit("on-customer-domain-full-access", fullAccess);
      },
      onSaveClicked() {
        this.updateCustomerDomainAccess();
      },
      resetAllFlags() {
        const customerAccess = distributionChannelUtil.resetCustomerAccessFlags(this.customerAccess);
        this.updateCustomerAccess(this.customer.uuid, customerAccess);
      },
      updateCustomerDomainAccess() {
        this.displayLoader = true;
        const customerAccessPayload = distributionChannelUtil.formatCustomerAccessPayload(this.customer.uuid, this.customerAccess);
        DistributionChannelsService.updateCustomerDomainAccess(customerAccessPayload)
        .then(() => {
          this.resetAllFlags();
        })
        .catch((e) => {
          this.$bvToast.toast(this.getMessageText(this.messages.ERROR_WHILE_LOADING), TOAST_OPTIONS.bottomCenter);
          console.error(e);
        })
        .finally(() => {
          this.displayLoader = false;
        });
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "@/styles/components/main";
  .customer-domain-collapse-details {
    box-sizing: border-box;
    position: relative;
    border-top: 2px solid var(--LINE_GREY, #cdcdcd);
    border-bottom: 2px solid var(--LINE_GREY, #cdcdcd);
    padding-bottom: 1rem;
    .access-info {
      padding: 1rem 0.5rem 0 0.5rem;
    }
    .m-table {
      margin: 1rem 0;
    }
    .m-group-btns {
      text-align: center;
      .loader {
        width: 1.5rem;
        height: 1.5rem;
        position: absolute;
        left: 50%;
        bottom: 1.2rem;
      }
    }
    .overlay {
      height: 100%;
      width: 100%;
      background-color: var(--WHITE, #fff);
      position: absolute;
      top: 0;
      opacity: 0.2;
    }
    .loader {
      position: absolute;
      top: calc(50% - 16px);
      right: calc(50% - 25px);
    }
  }
</style>
